import { useRef, useEffect, useMemo, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import type { Place } from '../types/Place';
import { MonthGroup } from './MonthGroup';
import { groupByMonth } from '../utils/groupByMonth';
import { DURATION_SLOW, DURATION_FAST } from '../utils/animationTiming';
import { useTimeoutRef } from '../hooks/useTimeoutRef';
import { ChevronUpIcon } from './icons';

interface JournalViewProps {
  places: Place[];
  onShowOnMap: (id: string) => void;
  highlightedPlaceId?: string | null;
  collapsedMonths?: Set<string>;
  onToggleMonthCollapse?: (monthKey: string) => void;
}

// Story 8-6: Scroll threshold for showing scroll-to-top button (pixels)
const SCROLL_THRESHOLD = 300;

export function JournalView({
  places,
  onShowOnMap,
  highlightedPlaceId,
  collapsedMonths = new Set(),
  onToggleMonthCollapse,
}: JournalViewProps) {
  const entryRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [setAnnouncementTimer] = useTimeoutRef();
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [scrollTopAnnouncement, setScrollTopAnnouncement] = useState('');

  // Story 8-6: Track scroll position to show/hide button
  const handleScroll = useCallback(() => {
    if (scrollContainerRef.current) {
      const shouldShow = scrollContainerRef.current.scrollTop > SCROLL_THRESHOLD;
      if (shouldShow !== showScrollTop) {
        setShowScrollTop(shouldShow);
        // Announce to screen readers when button appears (H3 accessibility fix)
        if (shouldShow) {
          setScrollTopAnnouncement('Scroll to top button available');
          // Clear after announcement
          setAnnouncementTimer(() => {
            setScrollTopAnnouncement('');
          }, 1000);
        }
      }
    }
  }, [showScrollTop, setAnnouncementTimer]);

  // Story 8-6: Scroll to top handler
  const handleScrollToTop = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }, []);

  // Sort by chronological order (oldest first) and group by month
  const monthGroups = useMemo(() => {
    const sortedPlaces = [...places].sort((a, b) => a.order - b.order);
    return groupByMonth(sortedPlaces);
  }, [places]);

  // Note: Timer cleanup is handled by useTimeoutRef hook automatically

  // Scroll to highlighted entry when it changes
  useEffect(() => {
    if (highlightedPlaceId) {
      const entry = entryRefs.current.get(highlightedPlaceId);
      if (entry && typeof entry.scrollIntoView === 'function') {
        // Small delay to ensure view transition completes
        requestAnimationFrame(() => {
          entry.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        });
      }
    }
  }, [highlightedPlaceId]);

  return (
    <motion.div
      ref={scrollContainerRef}
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: DURATION_SLOW / 1000 }}
      className="h-full overflow-y-auto bg-bg-base scroll-smooth overscroll-contain relative"
      data-testid="journal-view"
      onScroll={handleScroll}
    >
      <div className="max-w-[600px] mx-auto px-4 pt-4 pb-20">
        {/* Timeline container */}
        <div className="relative">
          {/* Vertical timeline line */}
          <div
            className="absolute left-6 md:left-8 top-0 bottom-0 w-0.5 timeline-gradient"
            aria-hidden="true"
            data-testid="timeline-line"
          />

          {/* Month Groups */}
          <div className="space-y-2">
            {monthGroups.length === 0 ? (
              <div
                className="text-center py-12"
                data-testid="journal-empty-state"
              >
                <p className="text-gray-400 text-lg">
                  No places to display
                </p>
              </div>
            ) : (
              monthGroups.map((group) => (
                <MonthGroup
                  key={group.key}
                  group={group}
                  onShowOnMap={onShowOnMap}
                  highlightedPlaceId={highlightedPlaceId}
                  entryRefs={entryRefs.current}
                  isCollapsed={collapsedMonths.has(group.key)}
                  onToggleCollapse={onToggleMonthCollapse}
                />
              ))
            )}
          </div>
        </div>
      </div>

      {/* H3 accessibility: Screen reader announcement for scroll button */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {scrollTopAnnouncement}
      </div>

      {/* Story 8-6: Scroll to top button */}
      <AnimatePresence>
        {showScrollTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: DURATION_FAST / 1000 }}
            onClick={handleScrollToTop}
            className="fixed bottom-20 right-4 lg:right-8 z-50 w-12 h-12 rounded-full bg-primary hover:bg-primary/80 text-white shadow-lg flex items-center justify-center transition-colors focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2 focus:ring-offset-bg-base"
            aria-label="Scroll to top"
            data-testid="scroll-to-top-button"
          >
            <ChevronUpIcon width={24} height={24} />
          </motion.button>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
